import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import HanziWriter from 'hanzi-writer';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { getStory, markStoryRead } from '../../api/stories';
import { updateCharacterState, markCharacterAsHard } from '../../api/characters';
import { applyPlacement } from '../../api/learners';
import { useLearner } from '../../hooks/useLearner';
import { useDelight } from '../../contexts/DelightContext';
import {
  getDisplayStateLabel,
  getDisplayStateColor,
  getDisplayStateBg,
} from '../../utils/stateHelpers';
import TagInStory from '../../components/common/TagInStory';
import TagStatus from '../../components/common/TagStatus';
import Button from '../../components/common/Button';
import EvalExplainerModal from '../../components/eval/EvalExplainerModal';
import useFocusOnMount from '../../hooks/useFocusOnMount';
import audioManager from '../../lib/audioManager';
import styles from './ReaderPage.module.css';

/* ─── Character Popup ─── */
function CharacterPopup({ character, childProfileId, storyId, onDismiss, onStateChange, mode = 'mobile', isEval = false }) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const writerRef = useRef(null);

  useEffect(() => {
    if (!character?.char || !writerRef.current) return;
    writerRef.current.innerHTML = '';
    const writer = HanziWriter.create(writerRef.current, character.char, {
      width: 120,
      height: 120,
      padding: 5,
      showOutline: true,
      strokeAnimationSpeed: 1,
      delayBetweenStrokes: 300,
    });
    writer.loopCharacterAnimation();
    return () => {
      if (writerRef.current) writerRef.current.innerHTML = '';
    };
  }, [character?.char]);

  const isDesktop = mode === 'desktop';

  // Desktop hint state
  if (isDesktop && !character) {
    return (
      <div className={`${styles.popupCard} ${styles.popupHint}`}>
        <p className={styles.popupHintText}>{t('reader.tapHint')}</p>
      </div>
    );
  }

  if (!character) return null;

  const state = character.state || 'to_learn';

  async function handleStateChange(newState) {
    if (saving || !childProfileId || !character.id) return;
    setSaving(true);
    try {
      if (newState === 'practicing') {
        await markCharacterAsHard(childProfileId, character.id, storyId);
      } else {
        await updateCharacterState(childProfileId, character.id, newState);
      }
      if (onStateChange) onStateChange(character.id, newState);
    } catch {
      // non-blocking
    }
    setSaving(false);
    if (!isDesktop) onDismiss();
  }

  const popupContent = (
    <div className={`${styles.popupCard} ${isDesktop ? '' : styles.popupMobile}`}>
      {/* Status tag — only for tracked characters (state set by this child) */}
      {character.display_state && (
        <div className={styles.popupStatusRow}>
          <TagStatus
            status={state === 'to_learn' ? 'unreviewed' : state === 'practicing' ? 'practicing' : 'known'}
          >
            {getDisplayStateLabel(character.display_state)}
          </TagStatus>
        </div>
      )}

      {/* Large character */}
      <p className={styles.popupChar}>{character.char}</p>

      {/* Pinyin */}
      {character.pinyin && (
        <p className={styles.popupPinyin}>{character.pinyin}</p>
      )}

      {/* Meaning */}
      {character.meaning && (
        <p className={styles.popupMeaning}>{character.meaning}</p>
      )}

      {/* Stroke order */}
      <p className={styles.popupStrokeLabel}>{t('reader.strokeOrder')}</p>
      <div ref={writerRef} className={styles.popupWriterBox} />

      {/* Action buttons — hidden in eval mode and for untracked characters */}
      {!isEval && character.state !== null && character.state !== undefined && (
        <div className={styles.popupActions}>
          <button
            type="button"
            className={styles.popupBtnSecondary}
            onClick={() => handleStateChange('practicing')}
            disabled={saving}
          >
            {state === 'practicing' ? t('reader.stillPracticing') : t('reader.practice')}
          </button>
          <button
            type="button"
            className={styles.popupBtnPrimary}
            onClick={() => handleStateChange('known')}
            disabled={saving}
          >
            {t('common.known')}
          </button>
        </div>
      )}
    </div>
  );

  if (isDesktop) return popupContent;

  return (
    <div role="dialog" aria-modal="true" aria-label={t('reader.charDetails')} onKeyDown={(e) => { if (e.key === 'Escape') onDismiss(); }}>
      <div className={styles.popupOverlayBg} onClick={onDismiss} aria-hidden="true" />
      {popupContent}
    </div>
  );
}

/* ─── Inline character tag logic (ADR-043 — display_state) ─── */
function getInlineCharTag(character) {
  if (!character.display_state) {
    // Untracked — no state tag, no inline highlight.
    return null;
  }
  return {
    label: getDisplayStateLabel(character.display_state),
    color: getDisplayStateColor(character.display_state),
    bg: getDisplayStateBg(character.display_state),
  };
}

/* ─── Inline Character ─── */
function InlineCharacter({ char, charInfo, showPinyin, onTap, isEval }) {
  // BP-TOUR-EVAL-01: in eval mode no character state exists yet, so suppress
  // the colored box/border styling. Tap + popup remain functional — only the
  // visual state indicator is hidden.
  const tag = !isEval && charInfo ? getInlineCharTag(charInfo) : null;
  const bg = tag?.bg || 'transparent';
  const hasBg = tag !== null && bg !== 'transparent';

  const isCJK = /[一-鿿]/.test(char);
  const isInteractive = isCJK;

  return (
    <span
      onClick={isInteractive ? () => onTap(charInfo || { char }) : undefined}
      onKeyDown={isInteractive ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTap(charInfo || { char }); } } : undefined}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      className={`${styles.inlineChar} ${isInteractive ? styles.inlineCharInteractive : styles.inlineCharStatic}`}
      style={{ '--char-bg': hasBg ? bg : 'transparent' }}
    >
      <span
        className={styles.inlineCharPinyin}
        style={{ visibility: showPinyin && charInfo?.pinyin ? 'visible' : 'hidden' }}
      >
        {charInfo?.pinyin || '\u00A0'}
      </span>
      <span className={styles.inlineCharGlyph}>
        {char}
      </span>
    </span>
  );
}

/* ─── Story Text Renderer ─── */
function StoryContent({ content, characters, showPinyin, onCharacterTap, isEval }) {
  if (!content) return null;

  const charMap = {};
  if (characters && Array.isArray(characters)) {
    characters.forEach((c) => {
      charMap[c.char] = c;
    });
  }

  // Strip inline pinyin annotations like （pīnyīn） before rendering
  const cleanContent = content.replace(/（[^）]*）/g, '');
  const rendered = [];
  for (let i = 0; i < cleanContent.length; i++) {
    const ch = cleanContent[i];
    const charInfo = charMap[ch];

    if (ch === '\n') {
      rendered.push(<br key={i} />);
      continue;
    }

    // Show pinyin for to_learn / practicing / untracked. Hide for known.
    const charShowPinyin = showPinyin && charInfo?.state !== 'known';

    rendered.push(
      <InlineCharacter
        key={i}
        char={ch}
        charInfo={charInfo}
        showPinyin={charShowPinyin}
        onTap={onCharacterTap}
        isEval={isEval}
      />
    );
  }

  return (
    <div className={styles.storyContentWrap}>
      {rendered}
    </div>
  );
}

/* ─── Main Reader Page ─── */
export default function ReaderPage() {
  const { t } = useTranslation();
  const mainRef = useFocusOnMount();
  const { storyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { data: activeLearner } = useLearner();
  const { enqueueDelight } = useDelight();

  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedChar, setSelectedChar] = useState(null);
  const [markReadError, setMarkReadError] = useState(null);
  const [evalTourDismissed, setEvalTourDismissed] = useState(false);
  const [skipping, setSkipping] = useState(false);

  // FP-DELIGHT-CONTEXT-01 (B2): guard against a double story-read submit (rapid
  // double-tap of Finish, or a re-render mid-flight). markStoryRead enqueues an
  // XP modal, so a second call would enqueue a second identical modal.
  const finishingRef = useRef(false);
  useEffect(() => () => { finishingRef.current = false; }, []);

  // Story-reading BGM (audio-system.md). Crossfades in on mount; fades out on
  // unmount so the map biome track resumes. No-ops while assets absent / muted /
  // story-music disabled.
  useEffect(() => {
    audioManager.playBGM('story-reading', 'reader');
    return () => audioManager.stopBGM();
  }, []);

  // BP-ONBOARDING-ATOMICITY-02: band must resolve from URL (?band=) when
  // resuming via OnboardingGuard cold-open — there is no location.state.
  // PlacementBandPage's continue path still passes it via state.
  const placementBand =
    location.state?.placementBand ||
    new URLSearchParams(location.search).get('band');

  // BP-TOUR-EVAL-01: explainer overlay shows on eval stories until placed.
  // Derived during render (instead of a setState-in-effect) so React doesn't
  // double-render and so re-entering the page after placement hides it.
  const showEvalTour =
    !!story?.is_eval && !activeLearner?.placement_band && !evalTourDismissed;

  // Options panel
  const [showOptions, setShowOptions] = useState(false);
  const [charSize, setCharSize] = useState('medium');
  const [localPinyinOn, setLocalPinyinOn] = useState(true);
  const optionsPanelRef = useRef(null);
  const optionsBtnRef = useRef(null);

  const globalPinyinOn = activeLearner?.pinyin_show !== false;
  const showPinyin = globalPinyinOn && localPinyinOn;

  const CHAR_SIZE_PX = { small: '20px', medium: '24px', large: '32px', xl: '40px' };

  useEffect(() => {
    if (!showOptions) return;
    function handleClickOutside(e) {
      if (
        optionsPanelRef.current && !optionsPanelRef.current.contains(e.target) &&
        optionsBtnRef.current && !optionsBtnRef.current.contains(e.target)
      ) {
        setShowOptions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showOptions]);

  useEffect(() => {
    if (activeLearner) {
      setCharSize(activeLearner.char_size || 'medium');
      setLocalPinyinOn(activeLearner.pinyin_show !== false);
    }
  }, [activeLearner]);

  useEffect(() => {
    if (!storyId || storyId === 'undefined') {
      navigate('/', { replace: true });
      return;
    }
    getStory(storyId)
      .then((data) => setStory(data))
      .catch((err) => setError(err.message || t('reader.loadError')))
      .finally(() => setLoading(false));
  }, [storyId, navigate]);

  const handleCharacterTap = useCallback((charInfo) => {
    setSelectedChar(charInfo);
  }, []);

  const handleCharacterStateChange = useCallback((charId, newState) => {
    // is_new_introduction is invariant for a story; display_state derives
    // deterministically from the new learning state plus the character's
    // existing is_new_introduction flag.
    const computeDisplayState = (charState, isNewIntro) => {
      if (charState === 'known') return 'known';
      if (charState === 'practicing') return 'practicing';
      if (isNewIntro === true) return 'new_introduction';
      return 'reinforcement';
    };
    setStory((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        characters: prev.characters.map((c) => {
          if (c.id !== charId) return c;
          return {
            ...c,
            state: newState,
            display_state: computeDisplayState(newState, c.is_new_introduction),
          };
        }),
      };
    });
    // Bug 3 fix: keep selectedChar in sync so the desktop popup badge
    // updates immediately after parent clicks Practice/Known.
    setSelectedChar((prev) => {
      if (prev?.id !== charId) return prev;
      return {
        ...prev,
        state: newState,
        display_state: computeDisplayState(newState, prev.is_new_introduction),
      };
    });
    queryClient.invalidateQueries({ queryKey: ['childCharacters'] });
    queryClient.invalidateQueries({ queryKey: ['learnerProfile'] });
  }, [queryClient]);

  // Count characters by display_state for legend (ADR-043).
  // I-16 exhaustiveness: known + practicing + new_introduction + reinforcement
  // = total tracked. Strip stays 3-bucket; reinforcement folds into new_in_story
  // (orange = "actively being taught" from the parent's perspective; popup
  // shows "New" for both new_introduction and reinforcement).
  const stateCounts = useMemo(() => {
    const counts = { known: 0, practicing: 0, new_in_story: 0 };
    if (story?.characters) {
      story.characters.forEach((c) => {
        if (!c.display_state) return; // untracked — not counted
        if (c.display_state === 'known') counts.known++;
        else if (c.display_state === 'practicing') counts.practicing++;
        else if (c.display_state === 'new_introduction' || c.display_state === 'reinforcement') counts.new_in_story++;
      });
    }
    return counts;
  }, [story]);

  async function handleFinish() {
    if (finishingRef.current) return;
    let updatedStory = story;
    // Eval stories skip markStoryRead — the placement flow doesn't grant XP and
    // the story copy isn't ever shown again. Just route straight to review.
    if (story?.is_eval) {
      navigate('/placement/review', {
        replace: true,
        state: {
          story,
          placementBand,
          learnerId: activeLearner?.id,
        },
      });
      return;
    }
    finishingRef.current = true;
    try {
      const result = await markStoryRead(storyId);
      if (result?.status) {
        updatedStory = { ...story, status: result.status };
      }
      if (result?.progression) {
        enqueueDelight(result.progression);
      }
      queryClient.invalidateQueries({ queryKey: ['childStories'] });
      queryClient.invalidateQueries({ queryKey: ['librarySeries'] });
      queryClient.invalidateQueries({ queryKey: ['librarySeriesDetail'] });
      // Story finished — the ship "sets sail" (audio-system.md → Story flow).
      audioManager.playSFX('story-complete');
    } catch (err) {
      finishingRef.current = false; // allow retry after a failed submit
      if (import.meta.env.DEV) console.error('Failed to mark story as read:', err);
      setMarkReadError(t('reader.saveError'));
      return;
    }
    navigate(`/story/celebrate/${storyId}`, {
      replace: true,
      state: { story: updatedStory },
    });
  }

  // BP-ONBOARDING-ATOMICITY-02: Skip placement review. The user has read the
  // eval story (brand value preserved); we stamp it as read, apply placement
  // with knownCount=0 + their band, then drop them on the dashboard.
  async function handleSkipEval() {
    if (skipping || !activeLearner?.id || !placementBand) return;
    setSkipping(true);
    setMarkReadError(null);
    try {
      // markStoryRead is idempotent — safe to call even if status is already 'read'.
      await markStoryRead(storyId);
      await applyPlacement(activeLearner.id, { skip: true, band: placementBand });
      // Force /users/me to refetch so onboarding_complete flips to true and
      // the dashboard becomes reachable through OnboardingGuard.
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'resume'] });
      navigate('/', { replace: true });
    } catch (err) {
      if (import.meta.env.DEV) console.error('Skip eval failed:', err);
      setMarkReadError(err?.message || t('reader.saveError'));
      setSkipping(false);
    }
  }

  if (loading) {
    return (
      <div className={styles.loadingState} aria-live="polite">
        <img
          src="/assets/chrono-compass-loader.webp"
          alt=""
          aria-hidden="true"
          className={styles.compassLoader}
        />
        <p className={styles.loadingText}>{t('reader.loadingStory')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorState}>
        <p className={styles.errorText}>{error}</p>
        <Button variant="primary" onClick={() => navigate('/', { replace: true })}>
          Go Back
        </Button>
      </div>
    );
  }

  const isUnreviewed = story?.status === 'read';

  return (
    <main className={styles.page} ref={mainRef} tabIndex={-1}>

      {showEvalTour && (
        <EvalExplainerModal
          buddyAvatar={activeLearner?.buddy?.slug}
          onComplete={() => setEvalTourDismissed(true)}
        />
      )}

      {/* ── Desktop: full-bleed background ── */}
      <div
        className={styles.bgImage}
        style={{
          backgroundImage: story?.image_url
            ? `url(${story.image_url})`
            : 'linear-gradient(135deg, #34312c 0%, #211e1a 100%)',
        }}
      />
      <div className={styles.bgOverlay} />

      {/* ── Desktop: back button + title on dark bg ── */}
      <div className={styles.desktopHeader}>
        {!story?.is_eval && (
          <Button variant="back" onClick={() => navigate('/')}>{t('common.back')}</Button>
        )}
        <div className={styles.desktopTitle}>
          <p className={styles.titleEn}>{story?.title || 'Story'}</p>
        </div>
      </div>

      {/* ── Eval skip — desktop top-right (mirrors mobile hero overlay below) ── */}
      {story?.is_eval && placementBand && (
        <button
          type="button"
          onClick={handleSkipEval}
          disabled={skipping}
          className={`${styles.skipBtn} ${styles.skipBtnDesktop}`}
        >
          {skipping ? t('common.loading') : t('reader.skipForNow', 'Skip for now')}
        </button>
      )}

      {/* ── Mobile: hero image section ── */}
      <div className={styles.hero}>
        <div
          className={styles.heroImage}
          style={{
            background: story?.image_url
              ? `linear-gradient(to right, #221f1b 25%, rgba(34,31,27,0) 100%), url(${story.image_url})`
              : 'linear-gradient(135deg, #34312c 0%, #211e1a 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        {!story?.image_url && <div className={styles.heroGradient} />}

        {!story?.is_eval && (
          <div className={styles.heroBackBtn}>
            <Button variant="back" onClick={() => navigate('/')}>{t('common.back')}</Button>
          </div>
        )}

        {/* Eval skip — mobile, overlaid top-right of hero image */}
        {story?.is_eval && placementBand && (
          <button
            type="button"
            onClick={handleSkipEval}
            disabled={skipping}
            className={`${styles.skipBtn} ${styles.skipBtnMobile}`}
          >
            {skipping ? t('common.loading') : t('reader.skipForNow', 'Skip for now')}
          </button>
        )}

        <div className={styles.heroInfo}>
          {isUnreviewed && <TagStatus status="unreviewed" />}
          <div className={styles.heroTitleStack}>
            <h1 className={styles.titleEn}>{story?.title || 'Story'}</h1>
          </div>
        </div>
      </div>

      {/* ── Desktop: two-column layout ── */}
      <div className={styles.columns}>
        {/* Left: story panel */}
        <div className={styles.storyPanel} style={{ '--char-size': CHAR_SIZE_PX[charSize] || '24px' }}>
          {!story?.is_eval && (
          <div className={styles.legendBarWrap}>
            <div className={styles.legendBar}>
              <TagInStory state="known" count={stateCounts.known} />
              <TagInStory state="practicing" count={stateCounts.practicing} />
              <TagInStory state="to_learn" count={stateCounts.new_in_story} />
            </div>
            <button
              ref={optionsBtnRef}
              type="button"
              className={`${styles.optionsBtn}${showOptions ? ` ${styles.optionsBtnActive}` : ''}`}
              onClick={() => setShowOptions((o) => !o)}
              aria-expanded={showOptions}
              aria-label={t('reader.readingOptions')}
            >
              {t('reader.options')}
            </button>

            {/* Options floating panel */}
            {showOptions && (
              <div ref={optionsPanelRef} className={styles.optionsPanel} role="dialog" aria-label={t('reader.readingOptions')}>
                {/* Mobile-only close button */}
                <button
                  type="button"
                  className={styles.optionsCloseMobile}
                  onClick={() => setShowOptions(false)}
                >
                  {t('common.close')}
                </button>

                {/* Character Size */}
                <div className={styles.optionsSection}>
                  <p className={styles.optionsSectionLabel}>{t('reader.characterSize')}</p>
                  <div className={styles.optionsSizeRow}>
                    {[
                      { key: 'small', label: t('settings.small') },
                      { key: 'medium', label: t('settings.medium') },
                      { key: 'large', label: t('settings.large') },
                      { key: 'xl', label: t('settings.xl') },
                    ].map(({ key, label }) => (
                      <button
                        key={key}
                        type="button"
                        className={`${styles.optionSizeBtn}${charSize === key ? ` ${styles.optionBtnActive}` : ''}`}
                        onClick={() => setCharSize(key)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Show Pinyin */}
                <div className={styles.optionsSection}>
                  <p className={styles.optionsSectionLabel}>{t('reader.showPinyin')}</p>
                  <div className={styles.optionsToggleRow}>
                    <button
                      type="button"
                      className={`${styles.optionToggleBtn}${localPinyinOn ? ` ${styles.optionBtnActive}` : ''}`}
                      onClick={() => setLocalPinyinOn(true)}
                    >
                      On
                    </button>
                    <button
                      type="button"
                      className={`${styles.optionToggleBtn}${!localPinyinOn ? ` ${styles.optionBtnActive}` : ''}`}
                      onClick={() => setLocalPinyinOn(false)}
                    >
                      Off
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          )}
          <div className={styles.storyText}>
            <StoryContent
              content={story?.content || story?.text || ''}
              characters={story?.characters || []}
              showPinyin={showPinyin}
              onCharacterTap={handleCharacterTap}
              isEval={!!story?.is_eval}
            />
          </div>
          <div className={`${styles.finishBtnWrap} ${story?.is_eval ? styles.finishBtnWrapEval : ''}`}>
            <div className={styles.finishBtnInner}>
              <button type="button" className={styles.finishBtn} onClick={handleFinish}>
                {t('reader.finishStory')}
              </button>
            </div>
          </div>
        </div>

        {/* Right: character popup sidebar (desktop only) */}
        <div className={styles.popupSidebar}>
          <CharacterPopup
            character={selectedChar}
            childName={activeLearner?.name}
            childProfileId={activeLearner?.id}
            storyId={storyId}
            onDismiss={() => setSelectedChar(null)}
            onStateChange={handleCharacterStateChange}
            mode="desktop"
            isEval={!!story?.is_eval}
          />
        </div>
      </div>

      {/* ── Mobile-only content (hidden on desktop via CSS) ── */}
      {/* Error toast */}
      {markReadError && (
        <div className={styles.errorToast} role="alert">{markReadError}</div>
      )}

      {/* Mobile popup overlay (hidden on desktop via CSS) */}
      <CharacterPopup
        character={selectedChar}
        childName={activeLearner?.name}
        childProfileId={activeLearner?.id}
        storyId={storyId}
        onDismiss={() => setSelectedChar(null)}
        onStateChange={handleCharacterStateChange}
        mode="mobile"
        isEval={!!story?.is_eval}
      />
    </main>
  );
}
